import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Alert,
  Tabs,
  Tab,
  CircularProgress,
  Button,
  Dialog,
  DialogContent,
  Chip,
  Divider,
  Paper
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Lock as LockIcon,
  FactCheck as FactCheckIcon,
  Inventory as InventoryIcon,
  ListAlt as ListAltIcon,
  Edit as EditIcon,
  RocketLaunch as RocketIcon
} from '@mui/icons-material';
import { Project, projectService } from '../../services/projectService';
import { questionnaireService } from '../../services/questionnaireService';
import BasicQuestions from '../Questionnaire/BasicQuestions';
import PhaseQuestions from '../Questionnaire/PhaseQuestions';
import ScopeSummary from '../ScopeReview/ScopeSummary';
import MaterialSchedule from '../ScopeReview/MaterialSchedule';

// --- INTERFACES ---

interface ProjectDetailsTabProps {
  project: Project;
  isQuestionnaireComplete: boolean;
  onQuestionnaireComplete?: () => void;
  onScopeVerified?: () => void;
}

interface PhaseStatusCardProps {
  projectId: string;
  phaseNumber: number;
  phaseName: string;
  onQuestionsReady: (data: any) => void;
}

// --- CONSTANTS ---

const WORKFLOW_LOADING_MESSAGES = [
  "🏗️ Contacting Planning Agent...",
  "📐 Consulting with Structural Engineer...",
  "🔨 Reaching out to Groundworks Engineer...",
  "🏠 Coordinating with Room Planning Specialist...",
  "⚡ Negotiating with Electrical Engineer...",
  "💧 Discussing with Plumbing Specialist...",
  "🌡️ Conferring with HVAC Expert...",
  "🔥 Checking with Fire Safety Officer...",
  "♿ Consulting Accessibility Specialist...",
  "🌱 Coordinating with Sustainability Advisor...",
  "📋 Reviewing Building Regulations...",
  "🎨 Consulting with Architectural Designer...",
  "🔊 Checking with Acoustic Engineer...",
  "💡 Coordinating with Lighting Designer...",
  "🌊 Discussing with Drainage Specialist...",
  "🧱 Consulting Masonry Expert...",
  "🪟 Coordinating with Glazing Specialist...",
  "🚪 Checking with Joinery Consultant...",
  "🎯 Finalizing Project Scope...",
  "📊 Analyzing Project Requirements...",
  "🔍 Reviewing Site Constraints...",
  "📝 Drafting Initial Specifications...",
  "🏛️ Consulting Conservation Officer...",
  "🌳 Coordinating with Landscape Architect...",
  "🔐 Checking Security Requirements...",
  "📡 Consulting with Technology Integrator...",
  "🎭 Reviewing Aesthetic Considerations...",
  "⚖️ Verifying Compliance Standards...",
  "🧪 Consulting Materials Specialist...",
  "🌍 Assessing Environmental Impact...",
];

const PHASE_LOADING_MESSAGES = [
  "🤖 Analyzing phase requirements...",
  "📚 Reviewing building regulations...",
  "🎯 Identifying key considerations...",
  "💡 Crafting intelligent questions...",
  "🔍 Examining project specifics...",
  "📐 Calculating technical parameters...",
  "🏗️ Structuring questionnaire flow...",
  "⚡ Optimizing question relevance...",
  "🎨 Tailoring to your project type...",
  "🔧 Fine-tuning question logic...",
  "📊 Analyzing scope implications...",
  "🌟 Personalizing your experience...",
  "🧠 Applying AI intelligence...",
  "📝 Generating detailed questions...",
  "🎪 Adding contextual help text...",
  "🔬 Validating technical accuracy...",
  "🎯 Ensuring comprehensive coverage...",
  "💼 Consulting industry standards...",
  "🏛️ Referencing compliance requirements...",
  "✨ Polishing final questions...",
];

// --- MAIN COMPONENT ---

const ProjectDetailsTab: React.FC<ProjectDetailsTabProps> = ({ 
  project, 
  isQuestionnaireComplete,
  onQuestionnaireComplete,
  onScopeVerified
}) => {
  // --- NEW TOP LEVEL STATE ---
  const [mainMode, setMainMode] = useState<'collection' | 'review'>('collection');
  const [reviewSubTab, setReviewSubTab] = useState(0); // 0 = Scope, 1 = Materials

  // --- EXISTING STATE ---
  const [activeTab, setActiveTab] = useState(0);
  const [basicQuestionsComplete, setBasicQuestionsComplete] = useState(false);
  const [workflow, setWorkflow] = useState<any>(null);
  const [workflowTabs, setWorkflowTabs] = useState<any[]>([]);
  const [workflowStatus, setWorkflowStatus] = useState<string>('not_started');
  const [loading, setLoading] = useState(true);
  const [phaseQuestions, setPhaseQuestions] = useState<Record<number, any>>({});
  const [loadingPhaseQuestions, setLoadingPhaseQuestions] = useState<number | null>(null);
  const [currentLoadingMessage, setCurrentLoadingMessage] = useState<string>(WORKFLOW_LOADING_MESSAGES[0]);
  const [currentPhaseLoadingMessage, setCurrentPhaseLoadingMessage] = useState<string>(PHASE_LOADING_MESSAGES[0]);
  const [showSuccessOverlay, setShowSuccessOverlay] = useState(false);
  const [savedPhaseNumber, setSavedPhaseNumber] = useState<number | null>(null);
  
  // State for final submission
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [hasUnassignedMaterials, setHasUnassignedMaterials] = useState(false);
  const [checkingMaterials, setCheckingMaterials] = useState(false);

  // LOGIC: Check against 'draft' AND 'details_collection'.
  // If status is NEITHER, then we are in a locked/advanced state.
  const isScopeLocked = !['draft', 'details_collection'].includes(project.status || 'draft');

  // Rotate loading messages during workflow generation
  useEffect(() => {
    if (workflowStatus === 'generating') {
      const interval = setInterval(() => {
        setCurrentLoadingMessage(prev => {
          const currentIndex = WORKFLOW_LOADING_MESSAGES.indexOf(prev);
          const nextIndex = (currentIndex + 1) % WORKFLOW_LOADING_MESSAGES.length;
          return WORKFLOW_LOADING_MESSAGES[nextIndex];
        });
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [workflowStatus]);

  // Rotate loading messages during phase questions generation
  useEffect(() => {
    if (loadingPhaseQuestions !== null) {
      const interval = setInterval(() => {
        setCurrentPhaseLoadingMessage(prev => {
          const currentIndex = PHASE_LOADING_MESSAGES.indexOf(prev);
          const nextIndex = (currentIndex + 1) % PHASE_LOADING_MESSAGES.length;
          return PHASE_LOADING_MESSAGES[nextIndex];
        });
      }, 2500);
      return () => clearInterval(interval);
    }
  }, [loadingPhaseQuestions]);

    // Check material status when entering review mode
  useEffect(() => {
    const checkMaterials = async () => {
      if (mainMode === 'review' && !isScopeLocked) {
        setCheckingMaterials(true);
        try {
          const data = await questionnaireService.getScopeAndMaterials(project.id);
          const materials = data.verified_material_schedule && data.verified_material_schedule.length > 0
            ? data.verified_material_schedule
            : data.material_schedule || [];
          
          const unassigned = materials.some((m: any) => !m.supply_status || m.supply_status === 'To Be Confirmed');
          setHasUnassignedMaterials(unassigned);
        } catch (error) {
          console.error("Failed to check material status:", error);
        } finally {
          setCheckingMaterials(false);
        }
      }
    };
    checkMaterials();
  }, [mainMode, project.id, isScopeLocked, reviewSubTab]); // Re-check if sub-tab changes (might have saved)


  // Load existing workflow on mount
  useEffect(() => {
    const loadExistingWorkflow = async () => {
      try {
        const session = await questionnaireService.getQuestionnaireSession(project.id);
        if (session) {
          if (session.basicQuestions && Object.keys(session.basicQuestions).length > 0) {
            setBasicQuestionsComplete(true);
          }
          if (session.workflow && session.workflowGenerated) {
            handleWorkflowCompleted(session.workflow);
            setWorkflowStatus('completed');
          } else if (session.workflowStatus === 'generating') {
            setWorkflowStatus('generating');
            setTimeout(() => {
              pollWorkflowStatus(project.id, session.id);
            }, 3000);
          } else if (session.workflowStatus === 'failed') {
            setWorkflowStatus('failed');
          }
        }
      } catch (error) {
        console.error('Error loading existing workflow:', error);
      } finally {
        setLoading(false);
      }
    };
    loadExistingWorkflow();
  }, [project.id]);

  const handleBasicQuestionsComplete = async (responses: Record<string, any>, workflowData?: any, sessionId?: string) => {
    setBasicQuestionsComplete(true);
    if (!sessionId) {
      setWorkflowStatus('failed');
      return;
    }
    setWorkflowStatus('generating');
    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    try {
      setTimeout(() => {
        pollWorkflowStatus(project.id, sessionId);
      }, 5000);
    } catch (error) {
      console.error('Error starting workflow generation:', error);
      setWorkflowStatus('failed');
    }
  };

  const pollWorkflowStatus = async (projectId: string, sessionId: string) => {
    const maxAttempts = 60;
    let attempts = 0;
    const poll = async () => {
      try {
        attempts++;
        const statusResult = await questionnaireService.checkWorkflowStatus(projectId);
        if (statusResult.status === 'completed' && statusResult.workflow) {
          handleWorkflowCompleted(statusResult.workflow);
          if (onQuestionnaireComplete) onQuestionnaireComplete();
        } else if (statusResult.status === 'failed') {
          setWorkflowStatus('failed');
        } else if (statusResult.status === 'generating' && attempts < maxAttempts) {
          setTimeout(poll, 3000);
        } else if (attempts >= maxAttempts) {
          setWorkflowStatus('failed');
        }
      } catch (error) {
        if (attempts < maxAttempts) setTimeout(poll, 3000);
        else setWorkflowStatus('failed');
      }
    };
    poll();
  };

  const handleWorkflowCompleted = (workflowData: any) => {
    setWorkflow(workflowData);
    setWorkflowStatus('completed');
    const tabs: any[] = [];
    if (workflowData.workflow_phases) {
      workflowData.workflow_phases.forEach((phase: any) => {
        if (phase.applies_to_project) {
          tabs.push({
            id: `phase_${phase.phase_number}`,
            name: phase.phase_name,
            description: phase.description,
            phaseNumber: phase.phase_number,
            buildingRegsParts: phase.building_regs_parts || [],
            keyConsiderations: phase.key_considerations || []
          });
        }
      });
    }
    setWorkflowTabs(tabs);
    loadExistingPhaseQuestions(tabs);
  };

  const loadExistingPhaseQuestions = async (tabs: any[]) => {
    const session = await questionnaireService.getQuestionnaireSession(project.id);
    const savedPhaseAnswers = (session as any)?.phaseAnswers || {};
    for (const tab of tabs) {
      try {
        const result = await questionnaireService.checkPhaseQuestionsStatus(project.id, tab.phaseNumber);
        if (result.status === 'completed' && result.questions) {
          const phaseData = { ...result, savedAnswers: savedPhaseAnswers[tab.phaseNumber] || {} };
          setPhaseQuestions(prev => ({ ...prev, [tab.phaseNumber]: phaseData }));
        }
      } catch (error) {
        console.log(`Error loading questions for phase ${tab.phaseNumber}:`, error);
      }
    }
  };

  const handleSubmitPhaseAnswers = async (phaseNumber: number, answers: Record<string, any>) => {
    try {
      await questionnaireService.savePhaseAnswers(project.id, phaseNumber, answers);
      if (onQuestionnaireComplete) onQuestionnaireComplete();

      setPhaseQuestions(prev => ({
        ...prev,
        [phaseNumber]: { ...prev[phaseNumber], savedAnswers: answers }
      }));

      const currentPhaseIndex = workflowTabs.findIndex(tab => tab.phaseNumber === phaseNumber);
      const isLastPhase = currentPhaseIndex >= 0 && currentPhaseIndex === workflowTabs.length - 1;

      setSavedPhaseNumber(phaseNumber);
      setShowSuccessOverlay(true);

      setTimeout(() => {
        setShowSuccessOverlay(false);
        if (!isLastPhase && currentPhaseIndex >= 0 && currentPhaseIndex < workflowTabs.length - 1) {
          setActiveTab(currentPhaseIndex + 2);
        } else if (isLastPhase) {
          setMainMode('review');
        }
      }, 2000);

    } catch (error: any) {
      console.error('Error saving phase answers:', error);
      alert(`Failed to save answers: ${error.message}`);
    }
  };

  const handleConfirmScope = async () => {
    if (!window.confirm("Are you sure you want to finalize the scope? You can still edit details later, but we will now proceed to generate your Statement of Work.")) {
      return;
    }

    setIsFinalizing(true);
    try {
      // 1. Snapshot the Scope/Materials to verified fields
      // This ensures we have data for the SoW generator even if user didn't manually 'Save' in the sub-tabs.
      console.log('Snapshotting scope and materials...');
      const currentData = await questionnaireService.getScopeAndMaterials(project.id);
      
      const payload = {
        // Use existing verified data if available, otherwise fallback to AI generated defaults
        verified_master_state: currentData.verified_master_state || currentData.master_state,
        verified_material_schedule: (currentData.verified_material_schedule && currentData.verified_material_schedule.length > 0) 
          ? currentData.verified_material_schedule 
          : currentData.material_schedule
      };

      await questionnaireService.saveScopeAndMaterials(project.id, payload);
      console.log('Scope snapshot saved.');

      // 2. Update status
      console.log('Updating project status to sow_generation...');
      await projectService.updateProject(project.id, {
        status: 'sow_generation'
      });
      console.log('Project status updated successfully');

      // 3. Callback
      if (onScopeVerified) {
        onScopeVerified();
      }

    } catch (error: any) {
      console.error("Failed to finalize scope:", error);
      const errorMessage = error.response?.data?.error?.message || error.message || "Unknown error";
      alert(`Something went wrong while finalizing the scope: ${errorMessage}. Please try again.`);
    } finally {
      setIsFinalizing(false);
    }
  };


  const isTabEnabled = (index: number) => {
    if (index === 0) return true;
    if (index === 1) return basicQuestionsComplete;
    const previousTabPhase = workflowTabs[index - 2];
    if (!previousTabPhase) return false;
    if (previousTabPhase.phaseNumber === 1) return basicQuestionsComplete;
    const prevPhaseData = phaseQuestions[previousTabPhase.phaseNumber];
    return prevPhaseData && prevPhaseData.savedAnswers && Object.keys(prevPhaseData.savedAnswers).length > 0;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* --- UNIFIED HEADER CARD --- */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          {/* 1. Top Navigation Tabs */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Tabs 
              value={mainMode} 
              onChange={(_, val) => setMainMode(val)}
            >
              <Tab 
                value="collection" 
                label="1. Project Details Collection" 
                icon={<ListAltIcon />} 
                iconPosition="start"
                sx={{ borderRadius: 1.5 }}
              />
              <Tab 
                value="review" 
                label="2. Scope & Material Finalisation" 
                icon={<FactCheckIcon />} 
                iconPosition="start"
                // FIX: Only enabled when ALL phases are complete
                disabled={!isQuestionnaireComplete}
                sx={{ borderRadius: 1.5 }}
              />
            </Tabs>
            
            {mainMode === 'collection' && (
               <Chip label="In Progress" color="primary" size="small" variant="outlined" />
            )}
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* 2. Context Description */}
          <Typography variant="body1" color="text.secondary" paragraph>
            {mainMode === 'collection' 
              ? "Please provide detailed information about your project to help us create a scope of work."
              : "Review the AI-generated scope assumptions and confirm your material choices before generating the final contract."
            }
          </Typography>

          {/* 3. Workflow Status Alerts (Only in Collection Mode) */}
          {mainMode === 'collection' && (
            <>
              {basicQuestionsComplete && workflowStatus === 'pending' && (
                <Alert severity="warning">⚠️ Workflow generation not started. Please refresh the page.</Alert>
              )}
              {basicQuestionsComplete && workflowStatus === 'generating' && (
                <Alert severity="info" sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <CircularProgress size={20} sx={{ mr: 2 }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>{currentLoadingMessage}</Typography>
                      <Typography variant="caption" color="text.secondary">Creating your personalized project workflow...</Typography>
                    </Box>
                  </Box>
                </Alert>
              )}
              {basicQuestionsComplete && workflowStatus === 'completed' && (
                <Alert severity="success">✅ Workflow generated! {workflowTabs.length} sections are now available.</Alert>
              )}
              {basicQuestionsComplete && workflowStatus === 'failed' && (
                <Alert severity="error">❌ Failed to generate workflow. Please try again.</Alert>
              )}
              {!isQuestionnaireComplete && workflowStatus === 'completed' && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Please answer all questions in the phases below to unlock Scope & Material Finalisation.
                </Alert>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* --- MODE 1: COLLECTION CONTENT --- */}
      {mainMode === 'collection' && (
        <Box>
          {/* Phase Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs
              value={activeTab}
              onChange={(e, val) => setActiveTab(val)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                borderBottom: 1, 
                borderColor: 'divider',
                '& .MuiTabs-scroller': { scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } },
              }}
            >
              <Tab
                label="Preliminary Questions"
                icon={basicQuestionsComplete ? <CheckCircleIcon color="success" /> : <AssignmentIcon />}
                iconPosition="start"
              />
              {workflowTabs.map((tab, index) => {
                const tabIndex = index + 1;
                const enabled = isTabEnabled(tabIndex);
                
                // Check if this phase has saved answers
                const hasAnswers = phaseQuestions[tab.phaseNumber]?.savedAnswers && 
                                   Object.keys(phaseQuestions[tab.phaseNumber].savedAnswers).length > 0;
                
                const isPhase1 = tab.phaseNumber === 1;
                const isComplete = isPhase1 || hasAnswers;

                // Determine which icon to show
                let TabIcon;
                if (isComplete) {
                  TabIcon = <CheckCircleIcon sx={{ color: 'success.main' }} />;
                } else if (!enabled) {
                  TabIcon = <LockIcon sx={{ fontSize: 16, opacity: 0.7 }} />;
                } else {
                  TabIcon = <EditIcon />;
                }

                return (
                  <Tab
                    key={tab.id}
                    label={tab.name}
                    disabled={!enabled}
                    icon={TabIcon}
                    iconPosition="start"
                    sx={{
                      textTransform: 'none', 
                      fontWeight: 600, 
                      borderRadius: '20px', 
                      mr: 1, 
                      minHeight: 40,
                      // Active State (Selected)
                      '&.Mui-selected': { 
                        color: '#fff', 
                        backgroundColor: 'primary.main',
                        // Force all icons white when selected
                        '& .MuiSvgIcon-root': { color: '#fff !important' } 
                      },
                      // Inactive State (Not Selected)
                      '&:not(.Mui-selected)': { 
                        backgroundColor: 'action.hover',
                        '& .MuiSvgIcon-root': { 
                          color: isComplete ? 'success.main' : (enabled ? 'primary.main' : 'text.disabled')
                        }
                      }
                    }}
                  />
                );
              })}
            </Tabs>
          </Box>

          {/* Tab Content */}
          {activeTab === 0 && (
            <BasicQuestions
              projectId={project.id}
              projectType={project.projectType}
              onComplete={handleBasicQuestionsComplete}
              isGeneratingWorkflow={workflowStatus === 'generating'}
              workflowCompleted={workflowStatus === 'completed'}
            />
          )}

          {activeTab > 0 && workflowTabs[activeTab - 1] && (
            <Box>
              {workflowTabs[activeTab - 1].phaseNumber === 1 ? (
                <>
                  <Card sx={{ mb: 3 }}>
                    <CardContent>
                      <Typography variant="h5" gutterBottom>{workflowTabs[activeTab - 1].name}</Typography>
                      <Typography variant="body1" paragraph>{workflowTabs[activeTab - 1].description}</Typography>
                      {workflowTabs[activeTab - 1].buildingRegsParts?.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="subtitle2" gutterBottom>Relevant Building Regulations:</Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {workflowTabs[activeTab - 1].buildingRegsParts.map((part: string, idx: number) => (
                              <Box key={idx} sx={{ px: 1.5, py: 0.5, bgcolor: 'primary.light', color: 'primary.contrastText', borderRadius: 1, fontSize: '0.875rem' }}>{part}</Box>
                            ))}
                          </Box>
                        </Box>
                      )}
                      {workflowTabs[activeTab - 1].keyConsiderations?.length > 0 && (
                        <Box sx={{ mt: 3 }}>
                          <Typography variant="subtitle2" gutterBottom>Key Considerations:</Typography>
                          <Box component="ul" sx={{ mt: 1, pl: 2 }}>
                            {workflowTabs[activeTab - 1].keyConsiderations.map((consideration: any, idx: number) => (
                              <Typography component="li" variant="body2" key={idx} sx={{ mb: 1 }}>
                                <strong>{consideration.category}:</strong> {consideration.item} - {consideration.description}
                              </Typography>
                            ))}
                          </Box>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                          variant="contained"
                          size="large"
                          onClick={() => {
                            const currentTabIndex = activeTab - 1;
                            const nextApplicableTab = workflowTabs.find((tab, idx) => idx > currentTabIndex && tab.applies_to_project !== false);
                            if (nextApplicableTab) {
                              const nextTabIndex = workflowTabs.indexOf(nextApplicableTab);
                              setActiveTab(nextTabIndex + 1);
                            }
                          }}
                          sx={{ minWidth: 200 }}
                        >
                          Okay, Let's Proceed
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </>
              ) : (
                <>
                  <Card sx={{ mb: 3 }}>
                    <CardContent>
                      <Typography variant="h5" gutterBottom>{workflowTabs[activeTab - 1].name}</Typography>
                      <Typography variant="body1" paragraph>{workflowTabs[activeTab - 1].description}</Typography>
                      {workflowTabs[activeTab - 1].buildingRegsParts?.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="subtitle2" gutterBottom>Relevant Building Regulations:</Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {workflowTabs[activeTab - 1].buildingRegsParts.map((part: string, idx: number) => (
                              <Box key={idx} sx={{ px: 1.5, py: 0.5, bgcolor: 'primary.light', color: 'primary.contrastText', borderRadius: 1, fontSize: '0.875rem' }}>{part}</Box>
                            ))}
                          </Box>
                        </Box>
                      )}
                      {workflowTabs[activeTab - 1].keyConsiderations?.length > 0 && (
                        <Box sx={{ mt: 3 }}>
                          <Typography variant="subtitle2" gutterBottom>Key Considerations:</Typography>
                          <Box component="ul" sx={{ mt: 1, pl: 2 }}>
                            {workflowTabs[activeTab - 1].keyConsiderations.map((consideration: any, idx: number) => (
                              <Typography component="li" variant="body2" key={idx} sx={{ mb: 1 }}>
                                <strong>{consideration.category}:</strong> {consideration.item} - {consideration.description}
                              </Typography>
                            ))}
                          </Box>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                  {(() => {
                    const currentPhaseNum = workflowTabs[activeTab - 1].phaseNumber;
                    const hasQuestions = !!phaseQuestions[currentPhaseNum];
                    if (!hasQuestions) {
                      return (
                        <PhaseStatusCard 
                          projectId={project.id}
                          phaseNumber={currentPhaseNum}
                          phaseName={workflowTabs[activeTab - 1].name}
                          onQuestionsReady={(data) => {
                            setPhaseQuestions(prev => ({ ...prev, [currentPhaseNum]: data }));
                          }}
                        />
                      );
                    }
                    return (
                      <PhaseQuestions
                        phaseName={phaseQuestions[currentPhaseNum].phase_name}
                        questions={phaseQuestions[currentPhaseNum].questions || []}
                        technicalSpecifications={phaseQuestions[currentPhaseNum].technical_specifications || {}}
                        estimatedCompletionTime={phaseQuestions[currentPhaseNum].estimated_completion_time}
                        initialAnswers={phaseQuestions[currentPhaseNum].savedAnswers || {}}
                        onSubmit={(answers) => handleSubmitPhaseAnswers(currentPhaseNum, answers)}
                      />
                    );
                  })()}
                </>
              )}
            </Box>
          )}
        </Box>
      )}

      {/* --- MODE 2: REVIEW CONTENT --- */}
      {mainMode === 'review' && (
        <Box>
           {/* SCOPE CONFIRMATION ACTION BAR */}
           <Paper 
            elevation={3} 
            sx={{ 
              p: 2, 
              mb: 3, 
              bgcolor: isScopeLocked ? 'grey.100' : 'primary.50', 
              border: 1, 
              borderColor: isScopeLocked ? 'grey.300' : 'primary.200',
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2
            }}
          >
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" color={isScopeLocked ? 'text.secondary' : 'primary.900'}>
                {isScopeLocked ? 'Scope Verified & Finalized' : 'Ready to generate your Statement of Work?'}
              </Typography>
              <Typography variant="body2" color={isScopeLocked ? 'text.secondary' : 'primary.700'}>
                {isScopeLocked 
                  ? "You have already finalized the scope for this project. Check the 'Scope & Schedule' tab to proceed." 
                  : hasUnassignedMaterials 
                    ? "Incomplete: Please assign a supplier to all items in the 'Material Schedule' tab."
                    : "Ensure you have reviewed both the Scope Summary and Material Schedule below before proceeding."}
              </Typography>
            </Box>
            <Button
              variant="contained"
              size="large"
              color={isScopeLocked ? 'inherit' : 'primary'}
              endIcon={isFinalizing ? <CircularProgress size={20} color="inherit" /> : isScopeLocked ? <CheckCircleIcon /> : <RocketIcon />}
              onClick={handleConfirmScope}
              disabled={isFinalizing || isScopeLocked || hasUnassignedMaterials}
              sx={{ minWidth: 220 }}
            >
              {isFinalizing ? 'Finalizing...' : isScopeLocked ? 'Scope Locked' : 'Finalize & Generate SoW'}
            </Button>
          </Paper>

          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs value={reviewSubTab} onChange={(_, val) => setReviewSubTab(val)}>
              <Tab label="Scope Summary" icon={<AssignmentIcon />} iconPosition="start" />
              <Tab label="Material Schedule" icon={<InventoryIcon />} iconPosition="start" />
            </Tabs>
          </Box>

          {/* PASSING READONLY PROP */}
          {reviewSubTab === 0 && <ScopeSummary projectId={project.id} isReadOnly={isScopeLocked} />}
          {reviewSubTab === 1 && <MaterialSchedule projectId={project.id} isReadOnly={isScopeLocked} />}
        </Box>
      )}

      {showSuccessOverlay && savedPhaseNumber && (
        <Dialog
          open={showSuccessOverlay}
          PaperProps={{ sx: { borderRadius: 2, p: 2, minWidth: 400 } }}
        >
          <DialogContent sx={{ textAlign: 'center', py: 4 }}>
            <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              {workflowTabs.findIndex(tab => tab.phaseNumber === savedPhaseNumber) === workflowTabs.length - 1 ? 'All Questions Completed!' : 'Answers Saved Successfully!'}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {workflowTabs.findIndex(tab => tab.phaseNumber === savedPhaseNumber) === workflowTabs.length - 1
                ? 'Thanks for answering all the questions! You can now go to the Scope of Work tab to generate your SoW.'
                : 'Moving to the next phase...'}
            </Typography>
            {workflowTabs.findIndex(tab => tab.phaseNumber === savedPhaseNumber) !== workflowTabs.length - 1 && (
              <>
                <Box sx={{ mb: 2 }}><CircularProgress size={40} /></Box>
                <Typography variant="body2" color="text.secondary">Redirecting in a moment...</Typography>
              </>
            )}
          </DialogContent>
        </Dialog>
      )}
    </Box>
  );
};

export default ProjectDetailsTab;

// --- PHASE STATUS CARD COMPONENT ---

const PhaseStatusCard: React.FC<PhaseStatusCardProps> = ({ 
  projectId, 
  phaseNumber, 
  phaseName, 
  onQuestionsReady 
}) => {
  const [status, setStatus] = useState<string>('checking');

  useEffect(() => {
    let interval: NodeJS.Timeout;
    const check = async () => {
      try {
        const result = await questionnaireService.checkPhaseQuestionsStatus(projectId, phaseNumber);
        setStatus(result.status);
        if (result.status === 'completed') {
          onQuestionsReady(result);
          if (interval) clearInterval(interval);
        }
      } catch (error) {
        console.error("Status check failed", error);
      }
    };
    check();
    interval = setInterval(check, 3000);
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [projectId, phaseNumber, onQuestionsReady]);

  if (status === 'generating' || status === 'checking' || status === 'not_started') {
    return (
      <Card sx={{ mb: 2 }} id={`phase-card-${phaseNumber}`}>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress size={40} sx={{ mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            {status === 'not_started' ? 'Waiting for previous phase...' : 'Analyzing Project Context...'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            AI is deeply analysing all your answers and corresponding documents again to prepare questions for {phaseName} for your project.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            It can take a few minutes until the questions appear.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (status === 'failed') {
    return (
      <Card sx={{ mb: 2 }} id={`phase-card-${phaseNumber}`}>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <Alert 
            severity="error" 
            action={
              <Button 
                color="inherit" 
                size="small" 
                onClick={() => {
                    setStatus('generating');
                    questionnaireService.startPhaseQuestionGeneration(projectId, phaseNumber, {
                        phase_name: phaseName
                    });
                }}
              >
                Retry Generation
              </Button>
            }
          >
            Failed to generate questions for {phaseName}.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return null;
};